class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    if (window.BSCartEnhancer && typeof window.BSCartEnhancer.afterRender === 'function') window.BSCartEnhancer.afterRender();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      if (window.BSCartEnhancer && typeof window.BSCartEnhancer.afterRender === 'function') window.BSCartEnhancer.afterRender();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* BrightSmile cart drawer enhancements: checkout progress, smart upsells, package protection, free gift sync */
window.BSCartEnhancer = window.BSCartEnhancer || (() => {
  let syncing = false;
  let checkoutContinuing = false;
  let afterRenderTimer;
  let observerStarted = false;

  const sections = ['cart-drawer', 'cart-icon-bubble'];

  function getConfig() {
    const node = document.getElementById('BSCartDrawerConfig');
    if (!node) return null;
    try {
      const config = JSON.parse(node.textContent || '{}');
      config.essentialProductIds = (config.essentialProductIds || []).filter(Boolean).map(String);
      config.essentialVariantIds = (config.essentialVariantIds || []).filter(Boolean).map(String);
      config.essentialHandles = (config.essentialHandles || []).filter(Boolean).map(normalizeHandle);
      config.giftProductHandles = (config.giftProductHandles || []).filter(Boolean).map(normalizeHandle);
      config.packageProtectionHandles = (config.packageProtectionHandles || []).filter(Boolean).map(normalizeHandle);
      config.giftVariantId = config.giftVariantId ? String(config.giftVariantId) : null;
      config.packageProtectionVariantId = config.packageProtectionVariantId ? String(config.packageProtectionVariantId) : null;
      return config.enabled ? config : null;
    } catch (error) {
      console.warn('BrightSmile cart config could not be parsed.', error);
      return null;
    }
  }

  function normalizeHandle(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/^\/products\//, '')
      .split('?')[0]
      .split('#')[0]
      .replace(/^\/+|\/+$/g, '');
  }

  function getItemHandle(item) {
    if (!item) return '';
    return normalizeHandle(item.handle || item.product_handle || item.url || '');
  }

  function fetchCart() {
    return fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' }).then((response) => response.json());
  }

  function fetchProductByHandle(handle) {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return Promise.resolve(null);
    return fetch(`/products/${normalizedHandle}.js`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  function resolveVariantId(currentVariantId, handles) {
    if (currentVariantId) return Promise.resolve(String(currentVariantId));
    const remainingHandles = (handles || []).map(normalizeHandle).filter(Boolean);

    function next() {
      const handle = remainingHandles.shift();
      if (!handle) return Promise.resolve(null);
      return fetchProductByHandle(handle).then((product) => {
        if (!product || !Array.isArray(product.variants)) return next();
        const variant = product.variants.find((candidate) => candidate.available) || product.variants[0];
        return variant && variant.id ? String(variant.id) : next();
      });
    }

    return next();
  }

  function renderSections(parsedState) {
    if (!parsedState || !parsedState.sections) return;
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(parsedState);
      return;
    }

    Object.entries(parsedState.sections).forEach(([sectionId, html]) => {
      const target = sectionId === 'cart-drawer' ? document.getElementById('CartDrawer') : document.getElementById(sectionId);
      if (!target || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = sectionId === 'cart-drawer' ? doc.querySelector('#CartDrawer') : doc.querySelector(`#${sectionId}`);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  function cartRequest(url, body) {
    return fetch(url, {
      ...fetchConfig(),
      body: JSON.stringify({
        ...body,
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        renderSections(state);
        return state;
      });
  }

  function addVariant(variantId, quantity = 1, properties = {}) {
    if (!variantId) return Promise.resolve();
    return cartRequest(routes.cart_add_url, {
      items: [{ id: Number(variantId), quantity, properties }],
    });
  }

  function changeLine(id, quantity) {
    if (!id) return Promise.resolve();
    return cartRequest(routes.cart_change_url, { id, quantity });
  }

  function isAutoGiftLine(item) {
    return item && item.properties && String(item.properties._bs_free_gift || '').toLowerCase() === 'true';
  }

  function getQualifyingQty(cart, config) {
    return (cart.items || []).reduce((qty, item) => {
      const productId = item.product_id ? String(item.product_id) : '';
      const variantId = item.variant_id ? String(item.variant_id) : '';
      const handle = getItemHandle(item);
      if (
        config.essentialProductIds.includes(productId) ||
        config.essentialVariantIds.includes(variantId) ||
        config.essentialHandles.includes(handle)
      ) {
        return qty + Number(item.quantity || 0);
      }
      return qty;
    }, 0);
  }

  function findLineByVariant(cart, variantId) {
    if (!variantId) return null;
    return (cart.items || []).find((item) => String(item.variant_id) === String(variantId));
  }

  function findLineByHandle(cart, handles) {
    const normalizedHandles = (handles || []).map(normalizeHandle).filter(Boolean);
    if (!normalizedHandles.length) return null;
    return (cart.items || []).find((item) => normalizedHandles.includes(getItemHandle(item)));
  }

  function findAutoGiftLine(cart, config, resolvedGiftVariantId) {
    return (cart.items || []).find((item) => {
      const variantMatches = resolvedGiftVariantId && String(item.variant_id) === String(resolvedGiftVariantId);
      const handleMatches = (config.giftProductHandles || []).includes(getItemHandle(item));
      return (variantMatches || handleMatches) && isAutoGiftLine(item);
    });
  }

  function syncGift() {
    const config = getConfig();
    if (!config || !config.autoGift || syncing) return Promise.resolve(false);

    syncing = true;
    return resolveVariantId(config.giftVariantId, config.giftProductHandles)
      .then((resolvedGiftVariantId) => {
        if (!resolvedGiftVariantId) {
          console.warn('BrightSmile free gift could not be resolved. Check the gift product handle or select the gift product in theme settings.');
          return false;
        }
        config.giftVariantId = String(resolvedGiftVariantId);

        return fetchCart().then((cart) => {
          const qualifyingQty = getQualifyingQty(cart, config);
          const hasAnyGift = Boolean(findLineByVariant(cart, resolvedGiftVariantId) || findLineByHandle(cart, config.giftProductHandles));
          const autoGiftLine = findAutoGiftLine(cart, config, resolvedGiftVariantId);
          const qualifies = qualifyingQty >= Number(config.minQualifyingQty || 2);

          if (qualifies && !hasAnyGift) {
            return addVariant(resolvedGiftVariantId, 1, {
              _bs_free_gift: 'true',
              _bs_free_gift_label: 'Free Tongue Scraper',
            }).then(() => true);
          }

          if (!qualifies && autoGiftLine) {
            return changeLine(autoGiftLine.key, 0).then(() => true);
          }

          return false;
        });
      })
      .catch((error) => {
        console.warn('BrightSmile free gift sync failed.', error);
        return false;
      })
      .finally(() => {
        syncing = false;
      });
  }

  function afterRender() {
    window.clearTimeout(afterRenderTimer);
    afterRenderTimer = window.setTimeout(syncGift, 250);
  }

  function watchCartDrawerChanges() {
    if (observerStarted) return;
    const drawerNode = document.getElementById('CartDrawer');
    if (!drawerNode || !window.MutationObserver) return;
    observerStarted = true;
    const observer = new MutationObserver(() => afterRender());
    observer.observe(drawerNode, { childList: true, subtree: true });
  }

  function bindEvents() {
    document.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest('#CartDrawer-Checkout');
      if (checkoutButton) {
        const progress = document.querySelector('.bs-cart-progress');
        if (progress) progress.classList.add('bs-cart-progress--confirming');

        const config = getConfig();
        if (config && config.autoGift && !checkoutContinuing) {
          event.preventDefault();
          checkoutContinuing = true;
          checkoutButton.disabled = true;
          syncGift().finally(() => {
            window.location.href = '/checkout';
          });
        }
        return;
      }

      const upsellButton = event.target.closest('[data-bs-add-variant]');
      if (!upsellButton) return;
      event.preventDefault();
      const variantId = upsellButton.getAttribute('data-bs-add-variant');
      upsellButton.disabled = true;
      upsellButton.textContent = 'Adding';
      addVariant(variantId)
        .then(() => syncGift())
        .catch((error) => {
          console.warn('BrightSmile upsell add failed.', error);
          upsellButton.disabled = false;
          upsellButton.textContent = 'Add';
        });
    });

    document.addEventListener('change', (event) => {
      const toggle = event.target.closest('.bs-cart-package-toggle');
      if (!toggle) return;
      const config = getConfig();
      const variantId = toggle.getAttribute('data-bs-package-variant') || (config && config.packageProtectionVariantId);
      if (!variantId) return;

      toggle.disabled = true;
      const shouldAdd = toggle.checked;

      const promise = shouldAdd
        ? addVariant(variantId, 1, { _bs_cart_addon: 'package_protection' })
        : fetchCart().then((cart) => {
            const line = findLineByVariant(cart, variantId);
            return line ? changeLine(line.key, 0) : null;
          });

      promise
        .then(() => syncGift())
        .catch((error) => {
          console.warn('BrightSmile package protection update failed.', error);
          toggle.checked = !shouldAdd;
        })
        .finally(() => {
          toggle.disabled = false;
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindEvents();
      watchCartDrawerChanges();
      afterRender();
    });
  } else {
    bindEvents();
    watchCartDrawerChanges();
    afterRender();
  }

  return { afterRender, syncGift };
})();
